import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth';
import { useI18n, type Lang } from '../i18n';
import InstallHint from './InstallHint';
import StarShopModal from './StarShopModal';

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [shopOpen, setShopOpen] = useState(false);
  // STA-153 — avatar_url is server-canonical now. The legacy
  // 'mp-avatar-change' event still fires from Account so this surface
  // can repaint without waiting on the next user.refresh() round-trip;
  // we override-from-event then fall back to user.avatar_url.
  const [overrideAvatar, setOverrideAvatar] = useState<string | null>(null);

  useEffect(() => {
    const onChange = (e: Event) => {
      const ce = e as CustomEvent<string>;
      setOverrideAvatar(ce.detail ?? null);
    };
    window.addEventListener('mp-avatar-change', onChange);
    return () => window.removeEventListener('mp-avatar-change', onChange);
  }, []);
  const avatar = overrideAvatar ?? user?.avatar_url ?? null;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-tarot-gold/15 backdrop-blur supports-[backdrop-filter]:bg-tarot-bg/70 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-3">
          <Link
            to="/"
            className="mp-display text-xl sm:text-2xl text-tarot-gold no-underline tracking-wide whitespace-nowrap flex-none"
          >
            {t('brand')}
          </Link>
          <nav className="flex items-center gap-1.5 sm:gap-3 text-xs sm:text-sm">
            {user ? (
              <>
                <NavLink to="/reading" className={navCls}>{t('nav.draw')}</NavLink>
                <NavLink to="/friends" className={navCls}>{t('nav.friends')}</NavLink>
                <NavLink to="/history" className={navCls}>{t('nav.history')}</NavLink>
                <button
                  type="button"
                  onClick={() => setShopOpen(true)}
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border border-tarot-gold/45 bg-tarot-gold/10 text-tarot-gold text-[11px] sm:text-xs font-medium hover:bg-tarot-gold/20 hover:border-tarot-gold transition-colors whitespace-nowrap"
                  title={t('nav.stardust_pill_title')}
                  aria-label={t('nav.stardust_pill_title')}
                >
                  <span aria-hidden="true">★</span>
                  <span>{user.stardust ?? 0}</span>
                </button>
                <NavLink
                  to="/account"
                  className={({ isActive }) =>
                    `inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden border flex-none ${
                      isActive ? 'border-tarot-gold' : 'border-tarot-gold/30 hover:border-tarot-gold'
                    }`
                  }
                  aria-label={t('nav.account')}
                  title={t('nav.account')}
                >
                  {avatar ? (
                    <img src={avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="mp-display text-xs sm:text-sm text-tarot-gold">
                      {(user.display_name ?? user.email ?? '?').slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </NavLink>
                <div className="hidden sm:inline-flex">
                  <LangSwitch lang={lang} setLang={setLang} />
                </div>
                <button
                  onClick={logout}
                  className="hidden sm:inline text-tarot-muted hover:text-tarot-text text-xs whitespace-nowrap"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <NavLink to="/pricing" className={navCls}>{t('nav.pricing')}</NavLink>
                <div className="hidden sm:inline-flex">
                  <LangSwitch lang={lang} setLang={setLang} />
                </div>
                <Link to="/login" className="btn-ghost text-xs sm:text-sm">{t('nav.login')}</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <InstallHint />

      <footer className="border-t border-tarot-gold/15 mt-12">
        <div className="max-w-5xl mx-auto px-4 py-6 text-sm text-tarot-muted flex flex-wrap gap-4 justify-between">
          <span>{t('footer.copyright')}</span>
          <span className="flex gap-4">
            <Link to="/terms">{t('footer.terms')}</Link>
            <Link to="/privacy">{t('footer.privacy')}</Link>
            <Link to="/refund-policy">{t('footer.refund')}</Link>
          </span>
        </div>
      </footer>

      {shopOpen && <StarShopModal onClose={() => setShopOpen(false)} />}
    </div>
  );
}

const navCls = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap ${
    isActive
      ? 'text-tarot-gold no-underline'
      : 'text-tarot-text no-underline hover:text-tarot-gold'
  }`;

function LangSwitch({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div
      className="inline-flex items-center text-[11px] font-medium border border-tarot-gold/30 rounded-full overflow-hidden"
      role="group"
      aria-label="Language switcher"
    >
      <button
        type="button"
        onClick={() => setLang('en')}
        className={`px-2 py-1 ${lang === 'en' ? 'bg-tarot-gold/20 text-tarot-gold' : 'text-tarot-muted hover:text-tarot-gold'}`}
      >
        EN
      </button>
      <span className="w-px h-3 bg-tarot-gold/30" aria-hidden="true" />
      <button
        type="button"
        onClick={() => setLang('zh')}
        className={`px-2 py-1 ${lang === 'zh' ? 'bg-tarot-gold/20 text-tarot-gold' : 'text-tarot-muted hover:text-tarot-gold'}`}
      >
        中
      </button>
    </div>
  );
}
